export type IntakeCoverageBand = "none" | "partial" | "full";
export type IntakeChangePath =
  | "reuse-existing"
  | "extend-existing"
  | "compose-multiple"
  | "new-api";
export type IntakeCapabilityStatus = "covered" | "partial" | "missing";
export type IntakeMatchRole = "primary" | "supporting";

export interface IntakeCapability {
  id?: string;
  name: string;
  status: IntakeCapabilityStatus;
  matchedApiUuid?: string | null;
  matchedOperations?: string[];
  notes?: string;
}

export interface IntakeMatchedApi {
  uuid: string;
  name?: string;
  version?: string;
  coveragePercent?: number;
  role?: IntakeMatchRole;
  notes?: string;
}

export interface IntakeEvaluationPayload {
  schemaVersion?: string;
  evaluatedAt?: string;
  evaluatedBy?: string;
  satisfied: boolean;
  coverage: IntakeCoverageBand;
  coveragePercent: number;
  requirementSummary?: string;
  capabilities: IntakeCapability[];
  matchedApis: IntakeMatchedApi[];
  gaps?: string[];
  recommendedChangePath: IntakeChangePath;
  recommendedApiUuid?: string | null;
  rationale?: string;
}

const EVALUATION_BLOCK_RE = /```intake-evaluation\s*([\s\S]*?)```/i;

function normalizeCoverage(value: unknown): IntakeCoverageBand | null {
  const raw = String(value ?? "").trim().toLowerCase().replace(/_/g, "-");
  if (raw === "none" || raw === "partial" || raw === "full") return raw;
  return null;
}

function normalizePath(value: unknown): IntakeChangePath | null {
  const raw = String(value ?? "").trim().toLowerCase().replace(/_/g, "-");
  if (
    raw === "reuse-existing" ||
    raw === "extend-existing" ||
    raw === "compose-multiple" ||
    raw === "new-api"
  ) {
    return raw;
  }
  return null;
}

export function parseIntakeEvaluationJson(
  raw: string | null | undefined,
): IntakeEvaluationPayload | null {
  if (!raw?.trim()) return null;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const coverage = normalizeCoverage(parsed.coverage);
    const recommendedChangePath = normalizePath(parsed.recommendedChangePath);
    const coveragePercent = Number(parsed.coveragePercent);
    if (!coverage || !recommendedChangePath || Number.isNaN(coveragePercent)) {
      return null;
    }
    return {
      schemaVersion: typeof parsed.schemaVersion === "string" ? parsed.schemaVersion : undefined,
      evaluatedAt: typeof parsed.evaluatedAt === "string" ? parsed.evaluatedAt : undefined,
      evaluatedBy: typeof parsed.evaluatedBy === "string" ? parsed.evaluatedBy : undefined,
      satisfied: Boolean(parsed.satisfied),
      coverage,
      coveragePercent,
      requirementSummary:
        typeof parsed.requirementSummary === "string" ? parsed.requirementSummary : undefined,
      capabilities: Array.isArray(parsed.capabilities)
        ? (parsed.capabilities as IntakeCapability[])
        : [],
      matchedApis: Array.isArray(parsed.matchedApis)
        ? (parsed.matchedApis as IntakeMatchedApi[])
        : [],
      gaps: Array.isArray(parsed.gaps) ? (parsed.gaps as string[]) : [],
      recommendedChangePath,
      recommendedApiUuid:
        typeof parsed.recommendedApiUuid === "string" ? parsed.recommendedApiUuid : null,
      rationale: typeof parsed.rationale === "string" ? parsed.rationale : undefined,
    };
  } catch {
    return null;
  }
}

export function extractIntakeEvaluationFromMessage(
  content: string,
): IntakeEvaluationPayload | null {
  const match = content.match(EVALUATION_BLOCK_RE);
  if (!match?.[1]) return null;
  return parseIntakeEvaluationJson(match[1].trim());
}

export function serializeIntakeEvaluation(evaluation: IntakeEvaluationPayload): string {
  return JSON.stringify(evaluation);
}

export function graphqlCoverage(band: IntakeCoverageBand): "NONE" | "PARTIAL" | "FULL" {
  return band.toUpperCase() as "NONE" | "PARTIAL" | "FULL";
}

export function graphqlChangePath(
  path: IntakeChangePath,
): "REUSE_EXISTING" | "EXTEND_EXISTING" | "COMPOSE_MULTIPLE" | "NEW_API" {
  return path.replace(/-/g, "_").toUpperCase() as
    | "REUSE_EXISTING"
    | "EXTEND_EXISTING"
    | "COMPOSE_MULTIPLE"
    | "NEW_API";
}

export const INTAKE_CHANGE_PATH_LABELS: Record<IntakeChangePath, string> = {
  "reuse-existing": "Reuse existing API",
  "extend-existing": "Extend existing API",
  "compose-multiple": "Compose multiple APIs",
  "new-api": "Design a new API",
};

export const INTAKE_CHANGE_PATH_HELP: Record<IntakeChangePath, string> = {
  "reuse-existing":
    "An API already in this catalog covers the need. Use it as-is rather than designing a new one.",
  "extend-existing":
    "A matched API covers part of the need. Add the missing operations on that API (Architect Enhance).",
  "compose-multiple":
    "Several existing APIs together cover this. Combine them instead of creating a new API first.",
  "new-api":
    "Nothing in the catalog covers this well. Design a new API (Architect Build).",
};

export function intakeAssessmentDisplaySummary(input: {
  name?: string;
  actors?: string;
  requirement: string;
}): string {
  const lines = ["Catalog coverage assessment"];
  if (input.name?.trim()) lines.push(input.name.trim());
  if (input.actors?.trim()) lines.push(`Who: ${input.actors.trim()}`);
  const requirement = input.requirement.trim();
  lines.push(requirement.length > 280 ? `${requirement.slice(0, 277)}…` : requirement);
  return lines.join("\n");
}

export function buildIntakeFollowUpPrompt(input: {
  name?: string;
  requirement: string;
  evaluation: IntakeEvaluationPayload;
}): string {
  return [
    "Follow-up chat on a completed catalog coverage assessment.",
    "Do not re-run a full search or emit a new intake-evaluation fence unless the user asks to reassess.",
    input.name?.trim() ? `Intake name: ${input.name.trim()}` : "",
    "Original requirement:",
    input.requirement.trim(),
    "",
    "Saved evaluation JSON:",
    JSON.stringify(input.evaluation),
    "",
    "Briefly confirm coverage %, recommended path, and the top gap in one short paragraph, then wait for the user's question.",
  ]
    .filter((line) => line !== "")
    .join("\n");
}

export function buildIntakeAssessmentPrompt(input: {
  name?: string;
  actors?: string;
  requirement: string;
  relatedDocs?: string;
}): string {
  const lines = [
    "This is a one-shot catalog coverage assessment. Do not ask clarifying questions.",
    "Decompose capabilities from the details below, search existing APIs in this tenant catalog,",
    "score coverage, and emit an intake-evaluation JSON fence. Record assumptions under gaps.",
    "",
  ];
  if (input.name?.trim()) {
    lines.push(`Intake name: ${input.name.trim()}`);
  }
  if (input.actors?.trim()) {
    lines.push(`Actors: ${input.actors.trim()}`);
  }
  lines.push("Requirement:", input.requirement.trim());
  if (input.relatedDocs?.trim()) {
    lines.push("", "---", "Related documents (Markdown):", "", input.relatedDocs.trim());
  }
  return lines.join("\n");
}

export const RELATED_DOCS_MARKER = "\n\n---\nRelated documents (Markdown):\n\n";

export function splitIntakeMessage(content: string): {
  requirement: string;
  relatedDocs?: string;
} {
  const idx = content.indexOf(RELATED_DOCS_MARKER);
  if (idx < 0) {
    return { requirement: content.trim() };
  }
  const relatedDocs = content.slice(idx + RELATED_DOCS_MARKER.length).trim();
  return {
    requirement: content.slice(0, idx).trim(),
    relatedDocs: relatedDocs || undefined,
  };
}
